import React from 'react';
import { ChevronRightIcon, PackageSearchIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { Shipment, ShipmentStatus } from '../data/operations';
type Filter = 'All' | 'Critical' | 'At risk' | 'Customs hold';
type ShipmentsTableProps = {
  shipments: Shipment[];
  filter: Filter;
  onFilterChange: (filter: Filter) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
};
const filters: Filter[] = ['All', 'Critical', 'At risk', 'Customs hold'];
const statusStyle: Record<ShipmentStatus, string> = {
  Critical: 'border-red-400/40 bg-red-400/5 text-red-300',
  'At risk': 'border-amber-400/40 bg-amber-400/5 text-amber-300',
  'Customs hold': 'border-violet-400/40 bg-violet-400/5 text-violet-300',
  'On track': 'border-lime-400/30 bg-lime-400/5 text-lime-300'
};
export function ShipmentsTable({
  shipments,
  filter,
  onFilterChange,
  selectedId,
  onSelect
}: ShipmentsTableProps) {
  const reduceMotion = useReducedMotion();
  return <section className="border border-slate-700/80 bg-[#09131c]" aria-labelledby="shipments-heading" data-id="element-109">
      <div className="flex flex-col gap-3 border-b border-slate-700/70 px-3 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-110">
        <div data-id="element-111">
          <h2 id="shipments-heading" className="font-display text-xs font-bold tracking-[0.16em] text-slate-100" data-id="element-112">
            PRIORITY & EXCEPTION SHIPMENTS
          </h2>
          <p className="mt-0.5 font-mono text-[9px] text-slate-500" data-id="element-113">
            SLA WATCHLIST · SORTED BY RISK EXPOSURE
          </p>
        </div>
        <div className="flex overflow-x-auto" role="group" aria-label="Filter shipments" data-id="element-114">
          {filters.map(item => <button key={item} onClick={() => onFilterChange(item)} className={`whitespace-nowrap border px-3 py-1.5 font-mono text-[9px] transition-colors focus:z-10 focus:outline-none focus:ring-1 focus:ring-cyan-400 ${filter === item ? 'border-cyan-400/60 bg-cyan-400/10 text-cyan-200' : '-ml-px border-slate-700 text-slate-500 hover:text-slate-200'}`} aria-pressed={filter === item} data-id="element-115">
              {item.toUpperCase()}
            </button>)}
        </div>
      </div>

      {shipments.length ? <>
          <div className="hidden overflow-x-auto md:block" data-id="element-116">
            <table className="w-full min-w-[900px] border-collapse text-left" data-id="element-117">
              <thead data-id="element-118">
                <tr className="border-b border-slate-700 bg-[#071019] font-mono text-[8px] uppercase tracking-[0.13em] text-slate-500" data-id="element-119">
                  <th className="px-3 py-2 font-medium" data-id="element-120">
                    Priority / reference
                  </th>
                  <th className="px-3 py-2 font-medium" data-id="element-121">Container</th>
                  <th className="px-3 py-2 font-medium" data-id="element-122">Route</th>
                  <th className="px-3 py-2 font-medium" data-id="element-123">Vessel</th>
                  <th className="px-3 py-2 font-medium" data-id="element-124">ETA UTC</th>
                  <th className="px-3 py-2 font-medium" data-id="element-125">Exception</th>
                  <th className="px-3 py-2 font-medium" data-id="element-126">Status</th>
                  <th className="w-10" data-id="element-127">
                    <span className="sr-only" data-id="element-128">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-129">
                <AnimatePresence initial={false} data-id="element-130">
                  {shipments.map(shipment => <motion.tr key={shipment.id} layout={!reduceMotion} initial={reduceMotion ? false : {
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onClick={() => onSelect(shipment.id)} className={`cursor-pointer border-b border-slate-800 text-[11px] transition-colors last:border-b-0 hover:bg-cyan-400/[0.035] ${selectedId === shipment.id ? 'bg-cyan-400/[0.06] shadow-[inset_2px_0_0_#22d3ee]' : ''}`} tabIndex={0} onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(shipment.id);
                }
              }} aria-selected={selectedId === shipment.id} data-id="element-131">
                      <td className="px-3 py-2.5" data-id="element-132">
                        <div className="flex items-center gap-2" data-id="element-133">
                          <span className={`border px-1.5 py-0.5 font-mono text-[8px] ${shipment.priority === 'P1' ? 'border-red-400/40 text-red-300' : 'border-amber-400/30 text-amber-300'}`} data-id="element-134">
                            {shipment.priority}
                          </span>
                          <span className="font-mono font-semibold text-slate-200" data-id="element-135">
                            {shipment.id}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-slate-400" data-id="element-136">
                        {shipment.container}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-137">
                        <span className="text-slate-300" data-id="element-138">
                          {shipment.origin}
                        </span>
                        <span className="mx-1.5 text-slate-600" data-id="element-139">→</span>
                        <span className="text-slate-300" data-id="element-140">
                          {shipment.destination}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-400" data-id="element-141">
                        {shipment.vessel}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-slate-300" data-id="element-142">
                        {shipment.eta}
                      </td>
                      <td className={`px-3 py-2.5 font-mono ${shipment.delay === '—' ? 'text-slate-600' : 'text-amber-300'}`} data-id="element-143">
                        {shipment.delay}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-144">
                        <span className={`inline-block border px-2 py-1 font-mono text-[8px] uppercase ${statusStyle[shipment.status]}`} data-id="element-145">
                          {shipment.status}
                        </span>
                      </td>
                      <td className="px-2" data-id="element-146">
                        <ChevronRightIcon className="h-4 w-4 text-slate-600" data-id="element-147" />
                      </td>
                    </motion.tr>)}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-800 md:hidden" data-id="element-148">
            {shipments.map(shipment => <button key={shipment.id} onClick={() => onSelect(shipment.id)} className={`w-full p-3 text-left focus:outline-none focus:ring-1 focus:ring-inset focus:ring-cyan-400 ${selectedId === shipment.id ? 'bg-cyan-400/[0.06]' : ''}`} data-id="element-149">
                <div className="flex items-center justify-between" data-id="element-150">
                  <span className="font-mono text-xs font-semibold text-slate-200" data-id="element-151">
                    {shipment.id}
                  </span>
                  <span className={`border px-2 py-1 font-mono text-[8px] uppercase ${statusStyle[shipment.status]}`} data-id="element-152">
                    {shipment.status}
                  </span>
                </div>
                <p className="mt-2 text-xs text-slate-400" data-id="element-153">
                  {shipment.origin} <span className="text-slate-600" data-id="element-154">→</span>{' '}
                  {shipment.destination}
                </p>
                <p className="mt-1 font-mono text-[9px] text-slate-500" data-id="element-155">
                  {shipment.vessel} · ETA {shipment.eta} · {shipment.delay}
                </p>
              </button>)}
          </div>
        </> : <div className="grid min-h-48 place-items-center p-6 text-center" data-id="element-156">
          <div data-id="element-157">
            <PackageSearchIcon className="mx-auto h-7 w-7 text-slate-600" data-id="element-158" />
            <p className="mt-3 text-sm font-semibold text-slate-300" data-id="element-159">
              No shipments found
            </p>
            <p className="mt-1 text-xs text-slate-500" data-id="element-160">
              Try another filter or search term.
            </p>
          </div>
        </div>}
    </section>;
}